import { useState } from "react";
import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import { Layout, PageHero } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Contact } from "../components/Contact";
import { TechMarquee } from "../components/Sections";
import { Reveal } from "../components/motion";
import { openConsultationModal } from "../components/ConsultationModal";
import { SECTOR_CASE_STUDIES, getSectorBySlug } from "../data";
import {
  ExternalLink, ArrowRight, ArrowLeft, CheckCircle2, Award, Radio, Brain, Check, FileText,
  ChevronRight, ChevronDown, Sparkles, Shield, HeartPulse, GraduationCap, Landmark, Building2,
  ShoppingBag, Factory, Truck, UtensilsCrossed, Wheat, HelpCircle, Layers, Palette
} from "lucide-react";
import { BrowserMock } from "../components/Visuals";

/* Sector Icons mapping for navigation */
const SECTOR_NAV_ICONS = {
  healthcare: HeartPulse,
  education: GraduationCap,
  "b2b-industry": Landmark,
  "real-estate": Building2,
  retail: ShoppingBag,
  manufacturing: Factory,
  logistics: Truck,
  government: Shield,
  hospitality: UtensilsCrossed,
  agriculture: Wheat,
  "arts-culture": Palette,
};

/* ─── Krishi JalSevak Dedicated Showcase Component ─────────────────────────────────── */
function KrishiJalSevakPageSection({ product }) {
  const [activeSlide, setActiveSlide] = useState(0);

  return (
    <div className="space-y-12">
      {/* Product Highlight Banner */}
      <div className="gborder glass-card-dark rounded-3xl p-6 sm:p-10 border border-[#FF5A1F]/35 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-[#FF5A1F]/10 blur-[130px] pointer-events-none" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-8 border-b border-white/10 relative z-10">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#FF5A1F]/20 text-[#FF5A1F] border border-[#FF5A1F]/40 flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5" /> NeuraVolt Proprietary Product
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                100% Offline GSM Control
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/30">
                AI Crop Intelligence
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-white tracking-tight">
              {product.name}
            </h2>
            <p className="text-neutral-400 text-base sm:text-lg mt-2 max-w-2xl leading-relaxed">
              {product.tagline}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Link
              to="/portfolio/krishi-jalsevak"
              className="px-5 py-3 rounded-full text-sm font-semibold bg-white/10 hover:bg-white/20 text-white transition-all border border-white/20 flex items-center gap-2"
            >
              <span>Read Case Study</span>
              <ArrowRight className="w-4 h-4 text-[#FF5A1F]" />
            </Link>
            <button
              onClick={() => openConsultationModal("Krishi JalSevak Inquiry")}
              className="px-6 py-3 rounded-full text-sm font-semibold bg-[#FF5A1F] text-white hover:bg-[#ff723f] transition-all shadow-lg shadow-[#FF5A1F]/30 flex items-center gap-2"
            >
              <span>Inquire / Request Demo</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Problem & Impact stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 py-8 relative z-10">
          {product.marketImpact.map((item, idx) => (
            <div key={idx} className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#FF5A1F] shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm text-neutral-300 font-medium leading-relaxed">{item}</span>
            </div>
          ))}
        </div>

        {/* 2-Column: Hardware vs AI Mobile App */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-10 pt-4">
          {/* Smart Hardware */}
          <div className="bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-[#FF5A1F]/30 rounded-2xl p-6 sm:p-8">
            <div className="flex items-center justify-between gap-2 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FF5A1F]/20 border border-[#FF5A1F]/40 flex items-center justify-center text-[#FF5A1F]">
                  <Radio className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">1. Smart Hardware (100% Offline)</h3>
                  <p className="text-xs text-neutral-400 font-mono">NO INTERNET REQUIRED • OPERATES ANYWHERE</p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#FF5A1F]/20 text-[#FF5A1F] border border-[#FF5A1F]/30">
                Field Tested
              </span>
            </div>
            <div className="space-y-3.5">
              {product.hardwareSpecs.map((spec, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#FF5A1F]/20 flex items-center justify-center text-[#FF5A1F] text-xs font-bold shrink-0 mt-0.5">
                    ✓
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white">{spec.title}</h4>
                    <p className="text-xs text-neutral-400 mt-0.5 leading-relaxed">{spec.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Mobile App */}
          <div className="bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-blue-500/30 rounded-2xl p-6 sm:p-8">
            <div className="flex items-center justify-between gap-2 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
                  <Brain className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">2. AI Farm Intelligence App</h3>
                  <p className="text-xs text-neutral-400 font-mono">ANDROID & IOS • MULTI-LINGUAL</p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                AI Powered
              </span>
            </div>
            <div className="space-y-3.5">
              {product.aiSpecs.map((spec, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 text-xs font-bold shrink-0 mt-0.5">
                    ✓
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white">{spec.title}</h4>
                    <p className="text-xs text-neutral-400 mt-0.5 leading-relaxed">{spec.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pricing Comparison */}
        <div className="mt-10 pt-10 border-t border-white/10 relative z-10">
          <div className="text-center max-w-xl mx-auto mb-8">
            <span className="text-xs font-mono uppercase tracking-wider text-[#FF5A1F] font-semibold">
              TRANSPARENT FARMER PRICING
            </span>
            <h3 className="text-2xl font-bold text-white mt-1">
              Select Your Krishi JalSevak Edition
            </h3>
            <p className="text-xs sm:text-sm text-neutral-400 mt-1.5">
              One-time hardware investment with zero recurring server charges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {product.editions.map((ed, idx) => (
              <div
                key={idx}
                className={`rounded-2xl p-6 sm:p-8 flex flex-col justify-between transition-all ${
                  ed.highlight
                    ? "bg-gradient-to-b from-[#FF5A1F]/20 to-black/60 border-2 border-[#FF5A1F] shadow-[0_10px_35px_rgba(255,90,31,0.25)] relative"
                    : "bg-white/[0.04] border border-white/10"
                }`}
              >
                {ed.highlight && (
                  <span className="absolute -top-3 right-6 px-3 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-[#FF5A1F] text-white shadow-md">
                    Most Popular
                  </span>
                )}
                <div>
                  <div className="flex items-baseline justify-between mb-4">
                    <h4 className="text-lg font-bold text-white">{ed.name}</h4>
                    <span className="text-xs text-neutral-400">{ed.type}</span>
                  </div>
                  <div className="text-3xl sm:text-4xl font-display font-extrabold text-white mb-6">
                    {ed.price}
                    <span className="text-xs text-neutral-400 font-normal ml-1.5">inclusive of hardware</span>
                  </div>
                  <ul className="space-y-2.5 mb-8">
                    {ed.features.map((feat, fidx) => (
                      <li key={fidx} className="flex items-center gap-2.5 text-xs sm:text-sm text-neutral-300">
                        <Check className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={() => openConsultationModal(`Order Krishi JalSevak - ${ed.name}`)}
                  className={`w-full py-3 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                    ed.highlight
                      ? "bg-[#FF5A1F] text-white hover:bg-[#ff723f] shadow-lg shadow-[#FF5A1F]/30"
                      : "bg-white/10 text-white hover:bg-white/20 border border-white/15"
                  }`}
                >
                  Order / Inquire for {ed.name}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Pitch Deck Slide Viewer */}
        <div className="mt-14 pt-10 border-t border-white/10 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-[#FF5A1F] font-semibold flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Official Pitch Deck Slides
              </span>
              <h3 className="text-xl font-bold text-white mt-1">
                Krishi JalSevak Pitch Presentation
              </h3>
            </div>
            {/* Slide tabs */}
            <div className="flex flex-wrap gap-2">
              {product.slides.map((s, idx) => (
                <button
                  key={s.page}
                  onClick={() => setActiveSlide(idx)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    activeSlide === idx
                      ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/30"
                      : "bg-white/5 text-neutral-400 hover:text-white hover:bg-white/10 border border-white/10"
                  }`}
                >
                  Page {s.page}: {s.title}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-white/15 bg-black/60 shadow-2xl relative">
            <img
              src={product.slides[activeSlide].src}
              alt={product.slides[activeSlide].title}
              className="w-full h-auto max-h-[700px] object-contain mx-auto transition-opacity duration-300"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Industry Detail Page ─────────────────────────────────────────────────────── */
export default function IndustryDetail() {
  const { slug } = useParams();
  const [openFaq, setOpenFaq] = useState(0);

  const data = getSectorBySlug(slug);
  if (!data) return <Navigate to="/industries" replace />;

  const otherSectors = Object.values(SECTOR_CASE_STUDIES).filter((s) => s.slug !== data.slug);

  // Dynamic Schema.org JSON-LD with FAQPage + WebPage for Google Rich Snippets
  const faqSchema = data.faqs && data.faqs.length > 0 ? {
    "@type": "FAQPage",
    "mainEntity": data.faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  } : null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `https://neuravolt.in/industries/${data.slug}`,
        "url": `https://neuravolt.in/industries/${data.slug}`,
        "name": data.seoTitle || `${data.title} Solutions | NeuraVolt`,
        "description": data.overviewDesc || data.tagline,
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://neuravolt.in" },
            { "@type": "ListItem", "position": 2, "name": "Industries", "item": "https://neuravolt.in/industries" },
            { "@type": "ListItem", "position": 3, "name": data.sector, "item": `https://neuravolt.in/industries/${data.slug}` }
          ]
        }
      },
      ...(faqSchema ? [faqSchema] : [])
    ]
  };

  return (
    <Layout>
      <Seo
        title={`${data.seoTitle || data.title} | NeuraVolt`}
        description={data.overviewDesc || `${data.tagline} Explore verified live platforms, client architectures, and enterprise case studies engineered by NeuraVolt in ${data.sector}.`}
        keywords={`${data.sector.toLowerCase()} software development, ${data.sector.toLowerCase()} app engineering, custom ${data.sector.toLowerCase()} solutions India, NeuraVolt technology`}
        path={`/industries/${data.slug}`}
        jsonLd={jsonLd}
      />

      {/* Mobile-Only Smooth Back Action (Hidden on desktop where users use native Chrome back arrow) */}
      <div className="md:hidden px-5 pt-20 -mb-16 relative z-20">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 hover:bg-white/15 active:scale-95 text-neutral-200 text-xs font-medium backdrop-blur-md border border-white/10 transition-all shadow-sm"
          aria-label="Back"
        >
          <ArrowLeft className="w-3.5 h-3.5 text-[#FF5A1F]" />
          <span>Back</span>
        </button>
      </div>

      {/* Hero */}
      <PageHero
        eyebrow={`INDUSTRY EXPERTISE • ${data.sector.toUpperCase()}`}
        title={data.title}
        sub={data.tagline}
      />

      {/* Sector Overview & Technical Capabilities Grid (SEO Rich) */}
      {data.capabilities && data.capabilities.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 -mt-8 mb-16 relative z-10">
          <div className="bg-white rounded-3xl p-6 sm:p-10 border border-black/10 shadow-xl">
            <div className="max-w-3xl mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F] text-xs font-bold uppercase tracking-wider mb-2">
                <Layers className="w-3.5 h-3.5" /> Architectural Capabilities
              </div>
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-neutral-900">
                Custom Engineering Tailored for {data.sector}
              </h2>
              {data.overviewDesc && (
                <p className="text-sm sm:text-base text-neutral-600 mt-2.5 leading-relaxed">
                  {data.overviewDesc}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {data.capabilities.map((cap, i) => (
                <div
                  key={i}
                  className="bg-neutral-50 border border-black/5 rounded-2xl p-5 hover:border-[#FF5A1F]/40 hover:shadow-md transition-all group"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/10 text-[#FF5A1F] flex items-center justify-center font-mono text-xs font-bold mb-3 group-hover:bg-[#FF5A1F] group-hover:text-white transition-colors">
                    0{i + 1}
                  </div>
                  <h3 className="text-base font-bold text-neutral-900 mb-1.5">
                    {cap.title}
                  </h3>
                  <p className="text-xs text-neutral-600 leading-relaxed">
                    {cap.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Sector Content (Case Studies / Product) */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-4 mb-20">
        {data.isProduct ? (
          <KrishiJalSevakPageSection product={data.product} />
        ) : (
          <div className="space-y-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-black/10">
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-[#FF5A1F] font-semibold">
                  SINGLE SOURCE OF TRUTH • VERIFIED PORTFOLIO
                </span>
                <h2 className="text-2xl sm:text-3xl font-display font-bold text-neutral-900 mt-1">
                  Active Platforms & Case Studies in {data.sector}
                </h2>
              </div>
              <Link
                to="/portfolio"
                className="text-xs font-semibold text-[#FF5A1F] hover:underline inline-flex items-center gap-1 shrink-0"
              >
                <span>View Full 50+ Projects Portfolio</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Project Cards - Single source of truth linked to /portfolio/:slug */}
            <div className={`grid gap-8 ${data.projects && data.projects.length > 1 ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
              {data.projects && data.projects.map((proj) => (
                <div
                  key={proj.slug}
                  className="rounded-3xl p-7 sm:p-9 bg-white border border-black/10 shadow-lg hover:shadow-xl hover:border-[#FF5A1F]/50 transition-all flex flex-col justify-between gap-6"
                >
                  <div>
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <h3 className="text-2xl font-display font-bold text-neutral-900">
                            {proj.title}
                          </h3>
                          <span className="px-3 py-0.5 rounded-full text-xs font-semibold bg-[#FF5A1F]/10 text-[#FF5A1F] border border-[#FF5A1F]/20">
                            {proj.badge || "NeuraVolt Built"}
                          </span>
                        </div>
                        <span className="text-xs text-neutral-500 font-mono block">
                          {proj.domain}
                        </span>
                      </div>

                      {proj.live_url && (
                        <a
                          href={proj.live_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold text-neutral-700 bg-neutral-100 hover:bg-neutral-200 border border-black/10 transition-all shrink-0"
                        >
                          <span>Live Site</span>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>

                    {/* Screenshot Preview */}
                    {proj.img && (
                      <Link to={`/portfolio/${proj.slug}`} className="block rounded-2xl overflow-hidden my-4 group/img border border-black/10 shadow-sm">
                        <BrowserMock
                          src={proj.img}
                          alt={`${proj.title} Preview`}
                          domain={proj.domain || "neuravolt.in"}
                          className="aspect-[16/10] w-full group-hover/img:scale-[1.01] transition-transform duration-300"
                        />
                      </Link>
                    )}

                    {/* Logo if available (e.g. Launchpad) */}
                    {proj.logo && !proj.img && (
                      <div className="bg-neutral-50 rounded-2xl p-4 my-4 flex items-center justify-center max-w-[280px] border border-black/5 shadow-inner">
                        <img
                          src={proj.logo}
                          alt={proj.title}
                          className="h-10 sm:h-12 w-auto object-contain"
                        />
                      </div>
                    )}

                    {/* Tagline */}
                    <p className="text-base font-semibold text-neutral-800 mt-2 mb-2">
                      {proj.tagline || proj.desc}
                    </p>

                    {/* Description */}
                    <p className="text-sm text-neutral-600 leading-relaxed mb-6">
                      {proj.overview || proj.desc}
                    </p>

                    {/* Metrics Bullet List */}
                    {proj.metrics && proj.metrics.length > 0 && (
                      <div className="space-y-2 mb-6 bg-neutral-50/70 p-4 rounded-2xl border border-black/5">
                        <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 font-semibold mb-1">
                          Key Proven Results:
                        </div>
                        {proj.metrics.map((m, idx) => (
                          <div key={idx} className="flex items-center gap-2.5 text-xs sm:text-sm text-neutral-700">
                            <CheckCircle2 className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                            <span>{m}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Dual CTA: Case Study link + Consultation */}
                  <div className="pt-5 border-t border-black/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex flex-wrap gap-1.5">
                      {proj.tech && proj.tech.map((t) => (
                        <span
                          key={t}
                          className="px-2.5 py-1 rounded-md text-xs font-medium bg-neutral-100 text-neutral-700 font-mono"
                        >
                          {t}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <Link
                        to={`/portfolio/${proj.slug}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold bg-[#0B0B0B] text-white hover:bg-[#FF5A1F] transition-all shadow-md hover:shadow-lg"
                      >
                        <span>Read Case Study</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Sector-Specific FAQ Accordion (SEO Rich) */}
      {data.faqs && data.faqs.length > 0 && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 py-12 mb-16">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="text-xs font-mono uppercase tracking-wider text-[#FF5A1F] font-semibold flex items-center justify-center gap-1.5">
              <HelpCircle className="w-3.5 h-3.5" /> DOMAIN INSIGHTS
            </span>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-neutral-900 mt-1">
              Frequently Asked Questions: {data.sector}
            </h2>
            <p className="text-xs sm:text-sm text-neutral-500 mt-1.5">
              Everything enterprise stakeholders ask before launching in this vertical.
            </p>
          </div>

          <div className="space-y-3">
            {data.faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-black/10 bg-white overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                    className="w-full p-5 sm:p-6 text-left flex items-center justify-between gap-4 font-semibold text-neutral-900 hover:text-[#FF5A1F] transition-colors"
                  >
                    <span className="text-sm sm:text-base">{faq.q}</span>
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 text-neutral-500 transition-transform ${
                        isOpen ? "rotate-180 text-[#FF5A1F]" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 sm:px-6 pb-6 text-xs sm:text-sm text-neutral-600 leading-relaxed border-t border-black/5 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Explore Other Industries */}
      <section className="bg-neutral-900 text-white py-16 px-5 sm:px-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-mono uppercase tracking-wider text-[#FF5A1F] font-semibold">
              EXPLORE OUR ECOSYSTEM
            </span>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mt-1">
              Engineered Across All Major Verticals
            </h2>
            <p className="text-neutral-400 text-sm mt-2">
              Browse other sector-specific architectures, case studies, and deployed software platforms.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {otherSectors.map((ind) => {
              const Icon = SECTOR_NAV_ICONS[ind.slug] || Sparkles;
              return (
                <Link
                  key={ind.slug}
                  to={`/industries/${ind.slug}`}
                  className="bg-white/5 hover:bg-[#FF5A1F]/15 border border-white/10 hover:border-[#FF5A1F]/50 rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center gap-2.5 transition-all text-center group"
                >
                  <Icon className="w-6 h-6 text-neutral-400 group-hover:text-[#FF5A1F] transition-colors" />
                  <span className="text-xs sm:text-sm font-semibold text-neutral-300 group-hover:text-white transition-colors">
                    {ind.sector}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <TechMarquee />
      <Contact />
    </Layout>
  );
}
